import React, { RefObject } from "react";
import { Box, Typography, SxProps, Theme, useTheme } from "@mui/material";
import type {
  TranscriptionSegmentTimed,
  TranscriptionTimedPayload,
} from "../../../../types";
import {
  transcriptBodyTypographyStyles,
  transcriptColumnOuterStyles,
  transcriptHeaderStyles,
  transcriptHeaderTitleStyles,
  transcriptScrollAreaStyles,
  transcriptTextCellStyles,
  transcriptTimeCellStyles,
  transcriptRowButtonStyles,
  transcriptTokenStyles,
} from "./styles";
import {
  fmtTs,
  getTranscriptHighlightPhase,
  resolveWordTimeInTranscriptCoords,
  tokenizeTranscriptWords,
  wordRangesByCharWeight,
} from "../../../../utils/transcription";

export interface InteractiveTranscriptPanelProps {
  timed: TranscriptionTimedPayload;
  activeIdx: number;
  activeRowRef: RefObject<HTMLButtonElement | null>;
  transcriptOffsetSec: number;
  // Transcript-timeline seconds (audio time minus offset); drives word/sentence highlight.
  playbackTranscriptSec: number;
  onSeekToSecond?: (sec: number) => void;
  columnSx?: SxProps<Theme>;
}

function TranscriptSegmentBody({
  seg,
  textWithoutPrefix,
  speaker,
  isActive,
  playbackTranscriptSec,
}: {
  seg: TranscriptionSegmentTimed;
  textWithoutPrefix: string;
  speaker: string;
  isActive: boolean;
  playbackTranscriptSec: number;
}) {
  const theme = useTheme();

  const speakerPrefix =
    speaker !== "" ? (
      <>
        <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
          {speaker}:
        </Box>{" "}
      </>
    ) : null;

  if (!isActive) {
    return (
      <>
        {speakerPrefix}
        {textWithoutPrefix}
      </>
    );
  }

  const apiWords = seg.words;
  if (apiWords?.length) {
    return (
      <>
        {speakerPrefix}
        {apiWords.map((w, i) => {
          const { start_sec, end_sec } = resolveWordTimeInTranscriptCoords(
            seg,
            w,
          );
          const phase = getTranscriptHighlightPhase(
            playbackTranscriptSec,
            start_sec,
            end_sec,
          );
          return (
            <React.Fragment key={`${seg.id ?? seg.start_sec}-w-${i}`}>
              {i > 0 ? " " : null}
              <Box component="span" sx={transcriptTokenStyles(theme, phase)}>
                {w.w}
              </Box>
            </React.Fragment>
          );
        })}
      </>
    );
  }

  const tokens = tokenizeTranscriptWords(textWithoutPrefix);
  if (tokens.length === 0) {
    return (
      <>
        {speakerPrefix}
        {textWithoutPrefix}
      </>
    );
  }

  const ranges = wordRangesByCharWeight(tokens, seg.start_sec, seg.end_sec);
  return (
    <>
      {speakerPrefix}
      {tokens.map((token, i) => {
        const { start_sec, end_sec } = ranges[i];
        const phase = getTranscriptHighlightPhase(
          playbackTranscriptSec,
          start_sec,
          end_sec,
        );
        return (
          <React.Fragment key={`${seg.id ?? seg.start_sec}-tok-${i}`}>
            {i > 0 ? " " : null}
            <Box component="span" sx={transcriptTokenStyles(theme, phase)}>
              {token}
            </Box>
          </React.Fragment>
        );
      })}
    </>
  );
}

export const InteractiveTranscriptPanel: React.FC<
  InteractiveTranscriptPanelProps
> = ({
  timed,
  activeIdx,
  activeRowRef,
  transcriptOffsetSec,
  playbackTranscriptSec,
  onSeekToSecond,
  columnSx,
}) => {
  return (
    <Box
      sx={
        (columnSx
          ? [transcriptColumnOuterStyles, columnSx]
          : transcriptColumnOuterStyles) as SxProps<Theme>
      }
    >
      <Box sx={transcriptHeaderStyles}>
        <Typography variant="subtitle1" sx={transcriptHeaderTitleStyles}>
          Interactive Transcript
        </Typography>
      </Box>

      <Box sx={transcriptScrollAreaStyles}>
        {timed.segments.map((seg, idx) => {
          const isActive = idx === activeIdx;
          const speaker = (seg.speaker || "").trim();
          const textWithoutPrefix =
            speaker && seg.text.startsWith(`${speaker}:`)
              ? seg.text.slice(speaker.length + 1).trim()
              : seg.text;

          const useBackendWords = Boolean(
            isActive && seg.words && seg.words.length > 0,
          );
          const tokens = tokenizeTranscriptWords(textWithoutPrefix);
          const granular = isActive && (useBackendWords || tokens.length > 0);

          return (
            <Box
              key={seg.id ?? `${seg.start_sec}-${idx}`}
              component="button"
              ref={isActive ? activeRowRef : null}
              onClick={() =>
                onSeekToSecond?.(
                  Math.max(0, seg.start_sec + transcriptOffsetSec),
                )
              }
              sx={transcriptRowButtonStyles(!!onSeekToSecond)}
            >
              <Box sx={transcriptTimeCellStyles(isActive)}>
                [{fmtTs(Math.max(0, seg.start_sec + transcriptOffsetSec))}]
              </Box>

              <Box sx={transcriptTextCellStyles(isActive)}>
                <Typography
                  variant="body2"
                  sx={
                    isActive && granular
                      ? {
                          color: "text.primary",
                          fontWeight: 400,
                          lineHeight: 1.45,
                        }
                      : transcriptBodyTypographyStyles(isActive)
                  }
                >
                  <TranscriptSegmentBody
                    seg={seg}
                    textWithoutPrefix={textWithoutPrefix}
                    speaker={speaker}
                    isActive={isActive}
                    playbackTranscriptSec={playbackTranscriptSec}
                  />
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
